import { useCallback } from "react";

// Imported from the leaf modules rather than the `clarity` barrel on purpose:
// the barrel also exports `useClarityIdentity`, which reaches into the Redux
// selector graph. Going through it would pull the store into every landing page
// component that fires a CTA event, and into their tests.
import { trackClarityEvents } from "../../clarity/clarityClient";
import { ClarityEventName, ClarityEvents } from "../../clarity/clarityEvents";

import { usePartnerLandingEntryNavigation } from "./usePartnerLandingEntryNavigation";

/**
 * Placement-specific Clarity events for the landing page calls to action.
 *
 * Every CTA on the page shares one destination, so placement is the only thing
 * that distinguishes them. Clarity events carry no properties, and a
 * `placement` custom tag would not work either: repeated `set` calls on one key
 * accumulate into an unordered array, so a visitor who clicks the hero CTA and
 * later the footer CTA yields both values with no way to tell which drove the
 * navigation. Only events feed funnels and the dashboard, so distinct event
 * names are the only way to attribute a click to the section that earned it.
 */
export const LandingCtaPlacements = {
  hero: ClarityEvents.landingCtaHero,
  build: ClarityEvents.landingCtaBuild,
  reach: ClarityEvents.landingCtaReach,
  operate: ClarityEvents.landingCtaOperate,
  join: ClarityEvents.landingCtaJoin,
  final: ClarityEvents.landingCtaFinal,
} as const;

export type LandingCtaPlacement = (typeof LandingCtaPlacements)[keyof typeof LandingCtaPlacements];

/**
 * Returns the click handler shared by every landing page call to action.
 *
 * Emits two events per click: the placement-specific one passed in, plus the
 * aggregate `landingCtaClicked`. The aggregate gives the onboarding funnel a
 * single clean entry step, while the placement events answer which section
 * actually converts.
 *
 * Navigation is delegated to `usePartnerLandingEntryNavigation` so the
 * destination stays owned by one hook: signed-in visitors go to the dashboard,
 * everyone else to sign-up. Landing sessions are usually anonymous, since
 * `identify` only runs once a partner profile has loaded, which makes these
 * events the primary signal available for the top of the funnel.
 */
export const useLandingCta = (placement: LandingCtaPlacement) => {
  const navigateToEntry = usePartnerLandingEntryNavigation();

  return useCallback(() => {
    trackClarityEvents(placement, ClarityEvents.landingCtaClicked);
    navigateToEntry();
  }, [navigateToEntry, placement]);
};

/** Emits a landing page engagement event that does not navigate. */
export const trackLandingEngagement = (eventName: ClarityEventName) =>
  trackClarityEvents(eventName);
